import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import CoverageMap from "@/components/CoverageMap";
import StationPanel from "@/components/StationPanel";
import { ConfidenceBadge } from "@/components/badges";
import { useAuth } from "@/context/AuthContext";
import { Link } from "react-router-dom";
import { PieChart, Pie, Cell, ResponsiveContainer, BarChart, Bar, XAxis, YAxis, Tooltip } from "recharts";
import { MapPinIcon, ShieldCheckIcon, WarningIcon, ClockIcon, DatabaseIcon, ClipboardTextIcon, ClockCountdownIcon, ArrowRightIcon, PlusIcon } from "@phosphor-icons/react";

const PIE = { Verified: "#059669", Unverified: "#d97706", Stale: "#e11d48" };

function Tile({ icon: Icon, label, value, tint }) {
  return (
    <div className="rounded-xl border border-slate-200 bg-white p-4">
      <div className={`inline-flex h-9 w-9 items-center justify-center rounded-lg ${tint}`}><Icon size={18} weight="duotone" /></div>
      <div className="mt-3 font-display text-2xl font-bold text-slate-900">{value}</div>
      <div className="text-xs font-medium uppercase tracking-wider text-slate-400">{label}</div>
    </div>
  );
}

export default function Dashboard() {
  const [stats, setStats] = useState(null);
  const [sites, setSites] = useState([]);
  const [alerts, setAlerts] = useState(null);
  const [states, setStates] = useState([]);
  const [region, setRegion] = useState("");
  const [access, setAccess] = useState("");
  const [panel, setPanel] = useState(null); // {mode, siteId}
  const { user } = useAuth();
  const canAdd = user?.role === "admin" || user?.role === "analyst";

  const loadGeo = () => api.get("/sites-geo", { params: { limit: 8000, ...(region ? { state: region } : {}), ...(access ? { access_type: access } : {}) } }).then((r) => setSites(r.data.results));

  useEffect(() => {
    api.get("/dashboard/stats").then((r) => setStats(r.data));
    api.get("/alerts/stale").then((r) => setAlerts(r.data));
    api.get("/regions").then((r) => setStates(r.data));
  }, []);

  useEffect(() => { loadGeo(); }, [region, access]);

  const mapCenter = sites.length
    ? { lat: sites.reduce((a, s) => a + s.latitude, 0) / sites.length, lng: sites.reduce((a, s) => a + s.longitude, 0) / sites.length }
    : { lat: 22.5, lng: 79.0 };
  const mapZoom = region ? 7 : 5;

  if (!stats) return <div className="p-8 text-slate-400 font-mono text-sm">Loading dashboard…</div>;
  const t = stats.totals;
  const pieData = [
    { name: "Verified", value: t.verified }, { name: "Unverified", value: t.unverified }, { name: "Stale", value: t.stale },
  ].filter((d) => d.value > 0);
  const ingData = [...stats.recent_ingestions].reverse().map((i, idx) => ({
    name: i.source_type?.toUpperCase() || `#${idx}`, ingested: i.counts?.ingested || 0, rejected: i.counts?.rejected || 0,
  }));

  return (
    <div className="p-5 lg:p-8 space-y-6">
      <div>
        <h1 className="font-display text-2xl lg:text-3xl font-semibold text-slate-900">Operations Dashboard</h1>
        <p className="text-sm text-slate-500 mt-1">Hyderabad pilot · fictional seed data · freshness SLA {stats.config.freshness_sla_hours}h</p>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3">
        <Tile icon={MapPinIcon} label="Sites" value={t.sites} tint="bg-slate-100 text-slate-600" />
        <Tile icon={ShieldCheckIcon} label="Verified" value={t.verified} tint="bg-emerald-50 text-emerald-600" />
        <Tile icon={ClockIcon} label="Stale" value={t.stale} tint="bg-rose-50 text-rose-600" />
        <Tile icon={WarningIcon} label="Open Discrepancies" value={t.open_discrepancies} tint="bg-amber-50 text-amber-600" />
        <Tile icon={DatabaseIcon} label="Sources" value={t.sources} tint="bg-sky-50 text-sky-600" />
        <Tile icon={ClipboardTextIcon} label="Audits" value={t.audits} tint="bg-violet-50 text-violet-600" />
      </div>

      {alerts && alerts.count > 0 && (
        <div data-testid="stale-alerts" className="rounded-xl border border-rose-200 bg-rose-50/60 overflow-hidden">
          <div className="flex items-center justify-between px-4 py-3 border-b border-rose-100">
            <h2 className="font-display font-medium text-rose-800 flex items-center gap-2">
              <ClockCountdownIcon size={18} weight="duotone" /> Stale Alerts — re-verify first
            </h2>
            <span className="text-xs text-rose-600">
              {alerts.recently_crossed} just crossed · {alerts.count} past SLA ({alerts.sla_hours}h)
            </span>
          </div>
          <div className="divide-y divide-rose-100">
            {alerts.alerts.slice(0, 6).map((a) => (
              <Link key={a.id} to={`/sites/${a.id}`} data-testid={`stale-${a.id}`}
                className="flex items-center justify-between px-4 py-2.5 text-sm transition-colors hover:bg-rose-100/50">
                <div className="min-w-0">
                  <span className="font-medium text-slate-800">{a.name}</span>
                  {a.recently_crossed && <span className="ml-2 rounded-full bg-rose-600 px-2 py-0.5 text-[10px] font-semibold uppercase text-white">Just crossed</span>}
                  <div className="text-xs text-slate-400">{a.operator || "Operator unknown"}</div>
                </div>
                <div className="flex items-center gap-3 shrink-0">
                  <span className="font-mono text-xs text-rose-600">{a.hours_over_sla}h over SLA</span>
                  <ArrowRightIcon size={14} className="text-rose-400" />
                </div>
              </Link>
            ))}
          </div>
        </div>
      )}

      <div className="grid lg:grid-cols-3 gap-4">
        <div className="lg:col-span-2 rounded-xl border border-slate-200 bg-white overflow-hidden">
          <div className="flex items-center justify-between px-4 py-3 border-b border-slate-100 gap-3">
            <h2 className="font-display font-medium text-slate-900">Coverage Map</h2>
            <div className="flex items-center gap-3">
              {canAdd && (
                <button data-testid="map-add-station" onClick={() => setPanel({ mode: "create" })}
                  className="inline-flex items-center gap-1.5 rounded-lg bg-emerald-600 px-3 py-1.5 text-xs font-semibold text-white hover:bg-emerald-700 transition-colors">
                  <PlusIcon size={14} weight="bold" /> Add Station
                </button>
              )}
              <select data-testid="map-region-filter" value={region} onChange={(e) => setRegion(e.target.value)}
                className="rounded-lg border border-slate-300 bg-white py-1.5 px-2.5 text-xs outline-none focus:border-emerald-500 max-w-[180px]">
                <option value="">All India</option>
                {states.map((s) => <option key={s.name} value={s.name}>{s.name} ({s.count.toLocaleString()})</option>)}
              </select>
              <select data-testid="map-access-filter" value={access} onChange={(e) => setAccess(e.target.value)}
                className="rounded-lg border border-slate-300 bg-white py-1.5 px-2.5 text-xs outline-none focus:border-emerald-500">
                <option value="">All access</option>
                <option value="Public">Public</option>
                <option value="Restricted">Restricted</option>
                <option value="Private">Private</option>
              </select>
              <span className="text-xs text-slate-400 whitespace-nowrap">{sites.length.toLocaleString()} shown</span>
            </div>
          </div>
          <CoverageMap key={region || "all"} sites={sites} center={mapCenter} zoom={mapZoom} height={420} cluster onSelect={(id) => setPanel({ mode: "view", siteId: id })} />
        </div>

        <div className="space-y-4">
          <div className="rounded-xl border border-slate-200 bg-white p-4">
            <h2 className="font-display font-medium text-slate-900 mb-2">Confidence Mix</h2>
            <div style={{ height: 180 }}>
              <ResponsiveContainer>
                <PieChart>
                  <Pie data={pieData} dataKey="value" nameKey="name" innerRadius={45} outerRadius={70} paddingAngle={3}>
                    {pieData.map((d) => <Cell key={d.name} fill={PIE[d.name]} />)}
                  </Pie>
                  <Tooltip />
                </PieChart>
              </ResponsiveContainer>
            </div>
            <div className="flex justify-center gap-3 text-xs">
              {pieData.map((d) => (
                <span key={d.name} className="flex items-center gap-1"><span className="h-2 w-2 rounded-full" style={{ background: PIE[d.name] }} />{d.name}</span>
              ))}
            </div>
          </div>
          <div className="rounded-xl border border-slate-200 bg-white p-4">
            <h2 className="font-display font-medium text-slate-900 mb-2">Ingestion Health</h2>
            <div style={{ height: 160 }}>
              <ResponsiveContainer>
                <BarChart data={ingData}>
                  <XAxis dataKey="name" tick={{ fontSize: 11 }} />
                  <YAxis tick={{ fontSize: 11 }} width={24} />
                  <Tooltip />
                  <Bar dataKey="ingested" fill="#059669" radius={[4, 4, 0, 0]} />
                  <Bar dataKey="rejected" fill="#e11d48" radius={[4, 4, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </div>
        </div>
      </div>

      <div className="rounded-xl border border-slate-200 bg-white overflow-hidden">
        <div className="px-4 py-3 border-b border-slate-100"><h2 className="font-display font-medium text-slate-900">Source Scorecards</h2></div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 text-left text-xs uppercase tracking-wider text-slate-400">
              <tr>
                <th className="px-4 py-2.5 font-semibold">Source</th>
                <th className="px-4 py-2.5 font-semibold">Type</th>
                <th className="px-4 py-2.5 font-semibold">Trust</th>
                <th className="px-4 py-2.5 font-semibold">Sites</th>
                <th className="px-4 py-2.5 font-semibold">Discrepancies</th>
                <th className="px-4 py-2.5 font-semibold">Last Ingested</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {stats.source_scorecards.map((s) => (
                <tr key={s.id} className="hover:bg-slate-50">
                  <td className="px-4 py-2.5 font-medium text-slate-800">{s.name}</td>
                  <td className="px-4 py-2.5"><span className="rounded bg-slate-100 px-2 py-0.5 text-xs uppercase text-slate-500">{s.type}</span></td>
                  <td className="px-4 py-2.5 capitalize text-slate-600">{s.trust_tier}</td>
                  <td className="px-4 py-2.5 font-mono">{s.site_count}</td>
                  <td className="px-4 py-2.5 font-mono">{s.discrepancy_count > 0 ? <span className="text-amber-600">{s.discrepancy_count}</span> : "0"}</td>
                  <td className="px-4 py-2.5 font-mono text-xs text-slate-400">{s.last_ingested_at ? new Date(s.last_ingested_at).toLocaleString() : "—"}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      <StationPanel open={!!panel} mode={panel?.mode} siteId={panel?.siteId}
        onClose={() => setPanel(null)} onSaved={() => { loadGeo(); api.get("/dashboard/stats").then((r) => setStats(r.data)); }} />
    </div>
  );
}
